import { KalturaAPIException } from '@kaltura-ott/ott-client-types';
import { KalturaHouseholdDevice } from '@kaltura-ott/ott-client-types/api/types';
import { KalturaOTTUser } from '@kaltura-ott/ott-client-types/api/types/KalturaOTTUser';
import {
  FFUser,
  KuxError,
  KuxConfig,
  kuxLogger,
  MediaType,
  toBoolean,
  userLogin,
  loginByPin,
  KuxErrorType,
  kuxUpdateConfig,
  MigrateOperation,
  sendMigrationEvent,
  tvpilGetLocalCache,
  DynamicDataTypeEnum,
  o2userResetPassword,
  HouseholdMigrationErrorMessage,
  HouseholdDeviceGetErrorCodeEnum,
} from '@kaltura-ott/tvpil';
import { LoginType, PerformanceEventArgs, UserLoginEventArgs } from '@kaltura-ott/tvpil-analytics';
import { action, observable } from 'mobx';
import { lastValueFrom, Subject } from 'rxjs';
import sha256 from 'sha256';
import { container, singleton } from 'tsyringe';

import { fetchFavoriteChannels } from '../../rhino/data/favorite';
import { fetchPersonalLineup } from '../../rhino/data/personalLineup';
import type { UserStore } from '../../stores/user/user-store';
import {
  LOGIN_FEATURES,
  LOGIN_METHOD,
  LOGIN_FORM_TYPE,
  OAuthLoginParams,
  UserLoginParams,
} from '../../types/stores/login-store-type';
import { assertPresence, errorHandling } from '../../utils/error-handling';
import { featureEnabler } from '../../utils/feature-enabler';
import { getMigrateParams } from '../../utils/migrate';
import type { AppParametersStore } from '../app/app-params-store';
import type { AppStore } from '../app/app-store';
import { LastWatchedLinearStore } from '../channelStats/last-watched-linear-store';
import { Features } from '../config';
import type { DmsStore } from '../dms/dms-store';
import { ErrorPriority, ErrorStore } from '../error';
import type { LinearRepository } from '../linearRepository/linear-repository';
import { LocalizationStore } from '../localization';
import { ProvidersRepository } from '../providersRepository';
import { RecordRepository } from '../recordRepository';
import type { TransactionStore } from '../transaction/transaction-store';

import { DeviceStore } from './device-store';
import { getActualAccessToken, getFormatedMacAddress, makeKey, signingCertificate } from './login-service';
import { ServicesStore } from './services-store';
import {
  oAuthLogin,
  silentLogin,
  appTokenAdd,
  jwtTokenLogin,
  appTokenDelete,
  anonymousUserLogin,
  getHouseholdDevice,
  appTokenStartSession,
  removeHouseholdDevice,
  usernamePasswordLogin,
} from './user-services';

const USERNAME_AND_PASSWORD_TOKEN = 'usernameAndPasswordToken';
const USERNAME_AND_PASSWORD_REFRESH_TOKEN = 'usernameAndPasswordRefreshToken';
const KS_TTL = 365 * 10 * 24 * 60 * 60 * 1000;

@singleton()
export class LoginStore {
  static moduleName = 'LoginStore';
  static certificateSignMethod?: (data: string, key: string) => Promise<string>;

  @observable ks = '';
  @observable userCode = '';
  @observable isAnonymous = true;
  @observable accessToken = '';
  @observable jwtToken?: { jwt: string; refreshToken: string };
  initialJwtToken?: { jwt: string; refreshToken: string };
  @observable isSilentLogin = false;
  @observable verificationUri = '';
  @observable userMigrationUri?: string;
  @observable isPostLoginProcessing = false;

  public firstSTBAppliedSettings = false;
  public isUserMigrationBlock = false;

  private sessionSubject = new Subject();

  private isLoginBySSOProcessing = false;

  private dmsStore!: DmsStore;
  private transactionStore?: TransactionStore;
  private errorStore?: ErrorStore;
  private deviceStore?: DeviceStore;
  private localizationStore?: LocalizationStore;
  private lastWatchedLinear?: LastWatchedLinearStore;
  private recordRepository?: RecordRepository;
  private appParametersStore?: AppParametersStore;
  private username?: string;
  private password?: string;
  private stbMigrationDone = false;

  // eslint-disable-next-line @typescript-eslint/ban-types
  private loginFlowMap: Map<string, Function>;

  constructor() {
    this.loginFlowMap = new Map();
    this.loginFlowMap.set(makeKey(LOGIN_METHOD.PIN_LOGIN), this.loginPin);
    this.loginFlowMap.set(makeKey(LOGIN_METHOD.EXTERNAL_LOGIN), this.loginOAuthUser);
    this.loginFlowMap.set(makeKey(LOGIN_METHOD.LOGIN_FORM, LOGIN_FORM_TYPE.OTT), this.loginByOttUsernameAndPassword);
    this.loginFlowMap.set(makeKey(LOGIN_METHOD.LOGIN_FORM, LOGIN_FORM_TYPE.SSO), this.loginBySSOUsernameAndPassword);
    this.dmsStore = container.resolve<DmsStore>('dmsStore');
  }

  private get appStore() {
    return container.resolve<AppStore>('appStore');
  }

  errorHandlerCallback(error?: KuxErrorType) {
    this.isPostLoginProcessing = false;
  }

  /**
   *  In case with external login you should provide `{oauth:OAuthParams}` (mobile, web). For authorize by pin code you should invoke without params (stb, smart).
   *  For SSO or OTT authorize by username and password you should provide `{form: FormParams}`.
   * @param params: UserLoginParams
   * @returns token
   */
  @action
  public userLogin(params?: UserLoginParams): void {
    const loginMethod: LOGIN_METHOD = featureEnabler.variation(
      LOGIN_FEATURES.LOGIN_METHOD,
      LOGIN_METHOD.EXTERNAL_LOGIN,
    );
    const loginFormType: LOGIN_FORM_TYPE = featureEnabler.variation(
      LOGIN_FEATURES.LOGIN_FORM_TYPE,
      LOGIN_FORM_TYPE.OTT,
    );

    const login = this.loginFlowMap.get(makeKey(loginMethod, loginFormType));
    if (login) {
      return login(params);
    }
  }

  init() {
    this.transactionStore = container.resolve<TransactionStore>('transactionStore');
    this.errorStore = container.resolve(ErrorStore);
    this.deviceStore = container.resolve<DeviceStore>('deviceStore');
    this.recordRepository = container.resolve<RecordRepository>('recordRepository');
    this.localizationStore = container.resolve<LocalizationStore>('localizationStore');
    this.lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
    this.appParametersStore = container.resolve<AppParametersStore>('appParamsStore');
  }

  toggleAnonymous(value: boolean) {
    this.isAnonymous = value;
  }

  setKs(ks: string) {
    this.ks = ks;
  }

  @action
  @errorHandling()
  prepareOAuthLogin({ codeChallenge, codeVerifier, authUrl }: OAuthLoginParams) {
    const cache = tvpilGetLocalCache();
    cache.setItem('codeVerifier', codeVerifier, 60000);

    const { oAuthScope, oAuthClientId, oAuthCodeChallengeMethod, oAuthResponseType, oauthBaseUrl } =
      featureEnabler.variation('AUTH_PARAMS', {});

    const url = new URL(oauthBaseUrl);
    const { searchParams } = url;

    searchParams.set('response_type', oAuthResponseType);
    searchParams.set('client_id', oAuthClientId);
    searchParams.set('redirect_uri', authUrl);
    searchParams.set('code_challenge', codeChallenge);
    searchParams.set('code_challenge_method', oAuthCodeChallengeMethod);
    searchParams.set('scope', oAuthScope);

    return url.href;
  }

  private get deviceLoginEventsAllowed() {
    return this.dmsStore.dms.analytics?.events?.allowDeviceUserActionEvents && !this.deviceStore?.deviceLimitExceeded;
  }

  @action.bound
  @errorHandling({
    type: ErrorPriority.high,
    skipRetry: true,
    callback: function (this: LoginStore, error) {
      this.errorHandlerCallback(error);
    },
  })
  async loginOAuthUser({ oauth }: UserLoginParams) {
    if (!oauth) return;
    const params = assertPresence(this.appParametersStore?.params);

    const cache = tvpilGetLocalCache();
    const codeVerifier = (await cache.getItem('codeVerifier')) as string;
    const loginData = await oAuthLogin(
      { ...oauth, codeVerifier },
      {
        partnerId: params.dmsSetting.partnerId,
        udid: params.udid,
      },
    );
    // @ts-expect-error TODO Fix
    await this.postLogin(loginData.ks, loginData.accessToken, loginData.userData);

    if (this.deviceLoginEventsAllowed) {
      this.appStore.kuxAnalytics?.send(new UserLoginEventArgs(LoginType.externalLogin));
    }

    // @ts-expect-error TODO Fix
    return loginData.ks;
  }

  private setCredentials(ks: string, appTokenId: string, appTokenHash: string, jwtToken = '', refreshJwtToken = '') {
    const cache = tvpilGetLocalCache();
    cache.setItem('ks', ks, KS_TTL);
    cache.setItem('appTokenId', appTokenId, KS_TTL);
    cache.setItem('appTokenHash', appTokenHash, KS_TTL);
    this.setJWTCredentials(jwtToken, refreshJwtToken);
  }

  private setJWTCredentials(jwt = '', refreshToken = '') {
    const cache = tvpilGetLocalCache();
    this.jwtToken = { jwt, refreshToken };
    cache.setItem(USERNAME_AND_PASSWORD_TOKEN, jwt, KS_TTL);
    cache.setItem(USERNAME_AND_PASSWORD_REFRESH_TOKEN, refreshToken, KS_TTL);
  }

  @action.bound
  public async loginAnonymousUser(appParams = assertPresence(this.appParametersStore?.params)) {
    await this.appStore?.clearAllCache({ skipCategoryTree: false, rent: false });
    this.resetStore();
    const anonymousKs = await anonymousUserLogin(appParams.dmsSetting);
    this.isAnonymous = true;
    this.ks = anonymousKs || '';
    kuxUpdateConfig({ ks: anonymousKs, customerType: undefined });
    this.setCredentials('', '', '');
    const servicesStore = container.resolve(ServicesStore);

    servicesStore?.init(this.ks);

    if (this.dmsStore?.dms?.launchDarkly && this.appParametersStore?.params?.featureEnabledOptions) {
      const newUser: FFUser = {
        key: appParams.udid,
        name: 'anonymous',
        anonymous: true,
      };
      await featureEnabler.updateUser(newUser);
    }
  }

  private async getSTBMigrationFlag(): Promise<boolean> {
    const cache = tvpilGetLocalCache();
    return !!(await cache.getItem('STB_MIGRATION_DONE'));
  }

  private async setSTBMigrationFlag(): Promise<void> {
    const macForMigration = this.appParametersStore?.params.silentLoginParams?.macLan;

    this.firstSTBAppliedSettings = true;
    sendMigrationEvent(MigrateOperation.SETTINGS_APPLIED, {
      stbMigrationUrl: this.dmsStore.dms.stbMigrationUrl,
      mac: getFormatedMacAddress(macForMigration),
      isSTBMigrationOperation: this.dmsStore.dms.isSTBMigrationOperation,
    });
    const cache = tvpilGetLocalCache();
    await cache.setItem('STB_MIGRATION_DONE', true, KS_TTL);
  }

  async getCredentials() {
    const cache = tvpilGetLocalCache();
    this.jwtToken = {
      jwt: (await cache.getItem(USERNAME_AND_PASSWORD_TOKEN)) as string,
      refreshToken: (await cache.getItem(USERNAME_AND_PASSWORD_REFRESH_TOKEN)) as string,
    };

    this.stbMigrationDone = !!(await cache.getItem('STB_MIGRATION_DONE')) || false;

    return {
      ks: (await cache.getItem('ks')) as string,
      appTokenId: (await cache.getItem('appTokenId')) as string,
      appTokenHash: (await cache.getItem('appTokenHash')) as string,
    };
  }

  /**
   *  For logout user from the system
   * @param deleteUser: boolean
   * @param clearApplicationRoute: boolean
   * @returns Promise<void>
   */
  @action.bound
  @errorHandling()
  async logoutUser(deleteUser = true, clearApplicationRoute = true) {
    const cache = tvpilGetLocalCache();
    this.resetStore();
    this.appStore.kuxAnalytics.hasUser = false;
    try {
      if (
        this.appParametersStore?.currentDevice?.udid &&
        deleteUser &&
        !featureEnabler.variation('NOT_EXPIRE_KS_AFTER_LOGOUT', false)
      ) {
        const appTokenId = (await cache.getItem('appTokenId')) as string;
        await Promise.all([
          appTokenDelete(appTokenId),
          removeHouseholdDevice(this.appParametersStore.currentDevice.udid),
        ]);
      }
    } catch (error) {
      console.log(error);
    }

    cache.removeItem('ks');
    cache.removeItem('appTokenId');
    cache.removeItem('appTokenHash');
    cache.removeItem('codeVerifier');
    cache.removeItem('parentalPinLimit');
    cache.removeItem('purchasePinLimit');
    cache.removeItem('parentalPinTimeout');
    cache.removeItem('purchasePinTimeout');
    cache.removeItem('subscriptions_sets');
    cache.removeAllBySubPrefix('purchase_');

    await this.appStore.clearAllCache({ skipCategoryTree: false, rent: false });
    await this.lastWatchedLinear?.resetStore();
    await this.loginAnonymousUser(this.appParametersStore?.params);

    await this.appStore.kuxAnalytics.reset();
    const servicesStore = container.resolve(ServicesStore);

    await servicesStore?.resetStore();
    this.transactionStore?.resetStore();

    if (clearApplicationRoute) {
      this.appStore.clearApplicationRoute();
    }

    await this.appStore.rebootApp();
  }

  /**
   * Device identifier
   */
  @errorHandling({
    type: ErrorPriority.low,
    skipRetry: true,
    callback: function (this: LoginStore, error) {
      this.errorHandlerCallback(error);
    },
  })
  @action.bound
  async loginSilentUser(): Promise<boolean> {
    if (!this.appParametersStore?.params.silentLoginParams || !this.dmsStore?.dms.silentLoginSettings.structure) {
      return false;
    }
    const params = assertPresence(this.appParametersStore?.params);

    const loginData = await silentLogin(
      {
        udid: params.udid,
        partnerId: params.dmsSetting.partnerId,
      },
      this.dmsStore?.dms?.silentLoginSettings,
      this.appParametersStore.params.silentLoginParams,
    );
    if (!loginData) {
      const stbMigrationDone = await this.getSTBMigrationFlag();
      if (!stbMigrationDone && this.dmsStore.dms.isSTBMigrationOperation) {
        const macForMigration = this.appParametersStore.params.silentLoginParams?.macLan;
        sendMigrationEvent(MigrateOperation.UNSUCCESSFULL_SILENT_LOGIN, {
          stbMigrationUrl: this.dmsStore.dms.stbMigrationUrl,
          mac: getFormatedMacAddress(macForMigration),
          isSTBMigrationOperation: this.dmsStore.dms.isSTBMigrationOperation,
        });
      }
      return false;
    }
    this.isSilentLogin = true;
    const startLoginSuccessTime = Date.now();
    await this.postLogin(loginData.ks);
    if (this.dmsStore?.dms?.analytics?.events?.loginSuccesses) {
      await this.appStore.kuxAnalytics?.send(
        new PerformanceEventArgs({
          metric: 'Login Success',
          value: (Date.now() - startLoginSuccessTime) / 1000,
        }),
      );
    }
    if (this.deviceLoginEventsAllowed) {
      this.appStore.kuxAnalytics?.send(new UserLoginEventArgs(LoginType.silentLogin));
    }
    return true;
  }

  @action.bound
  private setPinData = (userCode: string, verificationUri: string): void => {
    this.userCode = userCode;
    this.verificationUri = verificationUri;
  };

  /**
   * Start checking the login pin. Need to be called after opening the screen.
   * After starting userCode, verificationUri will be updated
   * @returns Promise
   */
  @action.bound
  @errorHandling({
    type: ErrorPriority.high,
    skipRetry: true,
    callback: function (this: LoginStore, error) {
      this.errorHandlerCallback(error);
    },
  })
  loginPin(): Promise<void> {
    this.loginPinStop();
    const params = assertPresence(this.appParametersStore?.params);

    return new Promise((resolve, reject) => {
      loginByPin(params.dmsSetting.partnerId, params.udid, this.sessionSubject, this.setPinData).subscribe(
        async (loginData) => {
          if (loginData === false) {
            return resolve();
          }

          this.loginPinStop();
          if (!loginData) {
            return reject();
          }
          if (loginData instanceof KalturaAPIException) {
            return reject(loginData);
          }
          const startLoginSuccessTime = Date.now();
          kuxUpdateConfig({ ks: loginData.ks } as KuxConfig);
          await this.postLogin(loginData.ks, loginData.accessToken);
          if (this.dmsStore?.dms?.analytics?.events?.loginSuccesses) {
            await this.appStore.kuxAnalytics?.send(
              new PerformanceEventArgs({
                metric: 'Login Success',
                value: (Date.now() - startLoginSuccessTime) / 1000,
              }),
            );
          }
          if (this.deviceLoginEventsAllowed) {
            this.appStore.kuxAnalytics?.send(new UserLoginEventArgs(LoginType.pinCode));
          }
          return resolve();
        },
        reject,
        resolve,
      );
    });
  }

  /**
   * Log user in by username and password
   *
   * @param username
   * @param password
   */
  @action.bound
  @errorHandling({
    type: ErrorPriority.high,
    skipRetry: true,
    callback: function (this: LoginStore) {
      this.isLoginBySSOProcessing = false;
    },
  })
  async loginBySSOUsernameAndPassword({ form }: UserLoginParams, skipServiceListFetch = false) {
    if (!form) {
      console.log('Params for SSO login is not defined!');
      return;
    }
    if (!this.appParametersStore?.currentDevice.udid) {
      return;
    }
    if (this.isLoginBySSOProcessing) {
      return;
    }
    const servicesStore = container.resolve(ServicesStore);

    const isWeb = this.appParametersStore?.params?.dmsSetting?.platform === 'other';
    let additionalParams = {};
    const { O2AuthServiceParam } = featureEnabler.variation('AUTH_PARAMS', {});

    const privateKey = this.dmsStore.dms.CKEY;
    const clientId = this.dmsStore.dms.clientId;
    this.isLoginBySSOProcessing = true;

    if (!isWeb && privateKey && clientId) {
      const { signatureTimestamp, signedString } = await signingCertificate({
        privateKey,
        serviceParam: O2AuthServiceParam,
        username: form.username,
        password: form.password,
        udid: this.appParametersStore?.currentDevice.udid,
        clientId,
        signMethod: LoginStore.certificateSignMethod,
      });
      additionalParams = { digSign: signedString, clientId, currentDate: signatureTimestamp };
    }

    const loginData = await usernamePasswordLogin({
      udid: this.appParametersStore?.currentDevice.udid,
      username: form.username,
      password: form.password,
      O2AuthServiceParam,
      ...additionalParams,
    });
    this.username = form.username;
    this.password = form.password;

    this.jwtToken = loginData.token;

    this.setJWTCredentials(this.jwtToken?.jwt, this.jwtToken?.refreshToken);
    if (!skipServiceListFetch) {
      await servicesStore?.fetchServicesListPage(this.jwtToken?.jwt);
    }

    if (!servicesStore?.servicesList?.length) {
      throw new KuxError('Service list error', 500);
    }

    this.isLoginBySSOProcessing = false;
    return {
      token: loginData.token,
    };
  }

  async setupServiceList(jwtToken: { jwt: string; refreshToken: string }) {
    const servicesStore = container.resolve(ServicesStore);
    this.jwtToken = jwtToken;
    this.setJWTCredentials(jwtToken.jwt, jwtToken.refreshToken);
    await servicesStore?.fetchServicesListPage(jwtToken.jwt);

    if (!servicesStore?.servicesList?.length) {
      throw new KuxError('Service list error', 500);
    }
    if (servicesStore.servicesList?.length === 1) {
      await servicesStore.selectService(servicesStore.servicesList[0].id);
    }
  }

  async refreshJwtToken() {
    if (this.username && this.password) {
      return this.loginBySSOUsernameAndPassword(
        {
          form: {
            username: this.username,
            password: this.password,
          },
        },
        true,
      );
    }

    return null;
  }

  @action.bound
  @errorHandling({
    type: ErrorPriority.high,
    skipRetry: true,
    callback: function (this: LoginStore, error) {
      this.errorHandlerCallback(error);
    },
  })
  async loginByOttUsernameAndPassword({ form }: UserLoginParams) {
    if (!form) {
      console.log('Params for OTT login is not defined!');
      return;
    }
    const loginResp = await lastValueFrom(
      userLogin({
        ...form,
        // @ts-expect-error TODO Fix
        partnerId: assertPresence(this.appParametersStore.params).dmsSetting.partnerId,
        // @ts-expect-error TODO Fix
        udid: this.appParametersStore.currentDevice.udid,
      }),
    );

    await this.postLogin(loginResp.loginSession.ks, undefined, loginResp.user);

    if (this.deviceLoginEventsAllowed) {
      this.appStore.kuxAnalytics?.send(new UserLoginEventArgs(LoginType.internalLogin));
    }

    return this.ks;
  }

  public async updateAccessToken(): Promise<void> {
    if (!this.jwtToken?.jwt || !this.jwtToken?.refreshToken) {
      return;
    }
    const params = assertPresence(this.appParametersStore?.params);
    const { accessToken, refreshToken } = await getActualAccessToken(
      params.dmsSetting.partnerId,
      this.jwtToken.jwt,
      this.jwtToken.refreshToken,
    );
    this.jwtToken = {
      jwt: accessToken,
      refreshToken: refreshToken,
    };

    this.accessToken = accessToken;
    this.setJWTCredentials(this.jwtToken.jwt, this.jwtToken.refreshToken);
  }

  @action
  async loginByJwtToken(serviceId: string) {
    if (!this.jwtToken?.jwt) return false;
    const params = assertPresence(this.appParametersStore?.params);
    const jwtLoginData = await jwtTokenLogin(
      {
        partnerId: params.dmsSetting.partnerId,
        udid: params.udid,
      },
      this.jwtToken?.jwt,
      assertPresence(this.appParametersStore?.currentDevice.brandId),
      assertPresence(this.appParametersStore?.currentDevice.udid),
      serviceId,
    );

    await this.postLogin(jwtLoginData.ks, this.jwtToken?.jwt, jwtLoginData.userData);

    if (this.deviceLoginEventsAllowed) {
      this.appStore.kuxAnalytics?.send(new UserLoginEventArgs(LoginType.externalLogin));
    }

    return true;
  }

  async getNewKs() {
    const { appTokenId, appTokenHash } = await this.getCredentials();
    const userStore = container.resolve<UserStore>('userStore');
    const anonymousKs = await anonymousUserLogin(assertPresence(this.appParametersStore?.params).dmsSetting);
    const startSessionResult = await appTokenStartSession({
      id: appTokenId,
      tokenHash: sha256(`${anonymousKs}${appTokenHash}`),
      udid: assertPresence(this.appParametersStore?.currentDevice.udid),
    });
    if (startSessionResult.ks) {
      kuxUpdateConfig({ ks: startSessionResult.ks } as KuxConfig);
      const cache = tvpilGetLocalCache();
      await cache.setItem('ks', startSessionResult.ks, KS_TTL);
      this.isAnonymous = false;
      if (this.deviceLoginEventsAllowed) {
        this.appStore.kuxAnalytics?.send(new UserLoginEventArgs(undefined, true));
      }
      await this.appStore.loadUserChannelStats();
      userStore?.updateLaunchdarklyUser();
      this.ks = startSessionResult.ks || '';
    }
    // await this.transactionStore?.loadMainSubscriptions();
    return startSessionResult?.ks;
  }

  async checkMigrationStatus() {
    this.isUserMigrationBlock = false;
    const stbMigrationDone = await this.getSTBMigrationFlag();
    const migrateUrl = this.dmsStore.dms.stbMigrationUrl;
    const macForMigration = this.appParametersStore?.params.silentLoginParams?.macLan;
    const userStore = container.resolve<UserStore>('userStore');

    kuxLogger('checkMigrationStatus', {
      migrateUrl,
      macForMigration,
      USER_MIGRATION_CHECK: featureEnabler.variation(Features.USER_MIGRATION_CHECK, false),
      stbMigrationDone,
    });
    if (!this.isAnonymous && featureEnabler.variation(Features.USER_MIGRATION_CHECK, false)) {
      const isMigrationCompleted = userStore?.getFromDynamicData(DynamicDataTypeEnum.migratedFlag);
      // if no flag just skip. React only for "false" value
      if (isMigrationCompleted && toBoolean(isMigrationCompleted) === false) {
        // link to redirect user or app link for error screen
        this.userMigrationUri = this.dmsStore?.dms?.o2Proxy;

        sendMigrationEvent(MigrateOperation.ACCESS_BLOCKED, {
          wrongAppAnalyticsUrl: this.dmsStore.dms.wrongAppAnalyticsUrl,
          subscriptionCode: userStore?.userData?.externalId,
          brand: this.appStore.params.deviceAttributes.brandId.toString(),
          family: this.dmsStore.dms.deviceFamilyId?.toString(),
          isSTBMigrationOperation: this.dmsStore.dms.isSTBMigrationOperation,
        });

        // throw error to client
        // throw new Error(HouseholdMigrationErrorMessage);
        this.isUserMigrationBlock = true;
      } else if (migrateUrl && macForMigration && !stbMigrationDone) {
        const settings = await getMigrateParams(migrateUrl, getFormatedMacAddress(macForMigration));
        kuxLogger('get settings', settings);

        if (!this.appStore.localizationStore.originalStore.languagesList?.length) {
          await this.appStore.localizationStore.originalStore.getLanguageList();
        }

        let audioLanguage = this.appStore.localizationStore.originalStore.defaultLanguage;
        let subtLanguage = this.appStore.localizationStore.originalStore.defaultLanguage;
        let displayLanguage = this.appStore.localizationStore.originalStore.defaultLanguage;

        if (settings.pl_aud_lang_p1) {
          audioLanguage =
            this.appStore.localizationStore.originalStore.languagesList.find(
              (lang) => lang.playerMigrationKey === settings.pl_aud_lang_p1,
            )?.code || audioLanguage;
        }

        if (settings.pl_subt_lang_p1) {
          subtLanguage =
            this.appStore.localizationStore.originalStore.languagesList.find(
              (lang) => lang.playerMigrationKey === settings.pl_subt_lang_p1,
            )?.code || subtLanguage;
        }

        if (settings.ui_language) {
          displayLanguage =
            this.appStore.localizationStore.originalStore.languagesList.find(
              (lang) => lang.migrationKey === settings.ui_language,
            )?.code || displayLanguage;
        }

        await this.appStore.localizationStore.originalStore.updateLanguages(
          displayLanguage,
          audioLanguage,
          subtLanguage,
        );

        const localVqSettings = this.dmsStore?.dms?.bitrateLimitationManagement?.localVqSettings;

        const streamQuality = settings.stream_quality;

        let bitrate;
        if (localVqSettings && streamQuality) {
          bitrate =
            streamQuality === 'UHD'
              ? localVqSettings['4k']?.top_range_bitrate
              : localVqSettings[streamQuality]?.top_range_bitrate;
          this.appStore.userStore.setBitrate(
            streamQuality === 'UHD'
              ? localVqSettings['4k']?.top_range_bitrate
              : localVqSettings[streamQuality]?.top_range_bitrate,
          );
        }
        kuxLogger('apply settings', {
          displayLanguage,
          audioLanguage,
          subtLanguage,
          bitrate,
        });

        await userStore?.loadUserData();

        await this.setSTBMigrationFlag();
      }
    }
  }

  async postLogin(ks: string, accessToken = '', userData?: KalturaOTTUser) {
    this.isPostLoginProcessing = true;
    this.username = '';
    this.password = '';
    this.ks = ks;
    this.accessToken = accessToken;

    await this.appStore.clearAllCache({ skipCategoryTree: false, rent: false });

    const userStore = container.resolve<UserStore>('userStore');
    kuxUpdateConfig({ ks } as KuxConfig);
    const appToken = await appTokenAdd();
    this.setCredentials(ks, appToken.id, appToken.token, this.jwtToken?.jwt, this.jwtToken?.refreshToken);
    this.isAnonymous = false;

    const promises: Array<Promise<unknown> | undefined> = [];
    const isLanguageChanged = await this.localizationStore?.checkLoginLanguage();

    await userStore?.loadUserData(userData);
    await userStore?.updateLaunchdarklyUser();
    await this.checkMigrationStatus();

    if (this.isUserMigrationBlock) {
      throw new Error(HouseholdMigrationErrorMessage);
    }

    if (isLanguageChanged) {
      await this.appStore.localizationStore.originalStore.init();
    }
    const linearRepository = container.resolve<LinearRepository>('linearRepository');

    const providersRepository = container.resolve<ProvidersRepository>('providersRepository');
    providersRepository.init();

    if (this.appStore.rootStore.isPersonalLineupEnabled) {
      promises.push(fetchPersonalLineup());
    }

    if (this.appStore.rootStore.isFavoriteChannelPersonalization) {
      promises.push(fetchFavoriteChannels());
    }

    promises.push(
      this.appStore.loadUserChannelStats(),
      this.transactionStore?.loadMainSubscriptions(),
      (async () => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await linearRepository.init(this.dmsStore!.dms.mediaTypes[MediaType.Linear]);
        const lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
        await lastWatchedLinear.init();
      })(),
      this.appStore.reloadCategoryTree(!isLanguageChanged),
      getHouseholdDevice()
        .then((device) => {
          this.deviceStore?.setHouseholdDevice(device as KalturaHouseholdDevice);
        })
        .catch((error) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((error as any).code === HouseholdDeviceGetErrorCodeEnum.DeviceNotExists) {
            return this.deviceStore?.addDevice();
          }
        }),
    );

    if (featureEnabler.variation('FEATURE_nPVR', false)) {
      promises.push(this.recordRepository?.getAvailableSpace());
    }

    await Promise.all(promises);
    this.isPostLoginProcessing = false;
  }

  /**
   * Stop checking the login pin. Need to be called after closing the screen
   */
  loginPinStop() {
    this.sessionSubject.next(null);
    this.sessionSubject.complete();

    this.sessionSubject = new Subject();
  }
  /**
   *  In order to reset password invoke
   * @param email: string
   */
  @action
  @errorHandling({ type: ErrorPriority.low, skipRetry: true })
  public async ssoResetPassword(email: string) {
    const result = await o2userResetPassword(
      email,
      assertPresence(this.appParametersStore?.params).dmsSetting.partnerId,
    );
    return result;
  }

  @action.bound
  resetStore() {
    this.userCode = '';
    this.verificationUri = '';
    this.jwtToken = undefined;

    this.deviceStore?.resetStore();

    const userStore = container.resolve<UserStore>('userStore');
    userStore?.resetStore();
  }
}
